import { Link, generatePath } from "react-router-dom";
import { Check, ExternalLink, X, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  PATH_PROJECT_LIMITS,
  PATH_PROJECT_RATES,
  PATH_PROJECT_SCHEDULES,
  PATH_PROJECT_STORES,
} from "@/constants";
import type { ProjectPeriodConfirmationRequiredCriterionCode } from "@/types/projectPeriodConfirmation";
import type { ProjectPeriodConfirmationSuggestedCriterionCode } from "@/types/projectPeriodConfirmation";
import { CriterionDetails } from "./CriterionDetails";

export const VARIANT_REQUIRED = "required";
export const VARIANT_SUGGESTED = "suggested";

const CRITERION_VIEW_PATHS: Partial<
  Record<ProjectPeriodConfirmationRequiredCriterionCode, string>
> = {
  has_at_least_one_assigned_store: PATH_PROJECT_STORES,
  all_stores_have_limit_with_rate: PATH_PROJECT_LIMITS,
  all_stores_have_schedule_with_at_least_one_visit: PATH_PROJECT_SCHEDULES,
  all_schedule_hours_within_limits: PATH_PROJECT_SCHEDULES,
  all_scheduled_store_roles_have_effective_rates: PATH_PROJECT_RATES,
  project_end_date_covers_confirmation_period: undefined,
};

type CriterionCode =
  | ProjectPeriodConfirmationRequiredCriterionCode
  | ProjectPeriodConfirmationSuggestedCriterionCode;

interface CriterionRowProps {
  code: CriterionCode;
  isMet: boolean;
  projectId?: string;
  details?: Record<string, unknown>;
  variant: typeof VARIANT_REQUIRED | typeof VARIANT_SUGGESTED;
}

export const CriterionRow = ({
  code,
  isMet,
  projectId,
  details,
  variant,
}: CriterionRowProps) => {
  const { t } = useTranslation();

  const pathToView =
    variant === VARIANT_REQUIRED
      ? CRITERION_VIEW_PATHS[
          code as ProjectPeriodConfirmationRequiredCriterionCode
        ]
      : undefined;

  const detailText = <CriterionDetails details={details} />;

  const icon =
    variant === VARIANT_SUGGESTED ? (
      <Lightbulb className="w-5 h-5 text-muted-foreground shrink-0 mt-1" />
    ) : isMet ? (
      <Check
        className="w-5 h-5 text-green-600 shrink-0 mt-1"
        aria-label={t("projectPeriodConfirmation.criterionMet")}
      />
    ) : (
      <X
        className="w-5 h-5 text-red-600 shrink-0 mt-1"
        aria-label={t("projectPeriodConfirmation.criterionNotMet")}
      />
    );

  const isSuccess = isMet && variant !== VARIANT_SUGGESTED;
  const isProblem = !isMet && variant !== VARIANT_SUGGESTED;

  return (
    <li className="flex flex-col gap-2 p-2 sm:grid sm:grid-cols-[1fr_auto] sm:gap-4">
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex flex-col gap-1">
          <span
            className={cn("text-lg text-foreground leading-tight", {
              "text-green-700": isSuccess,
              "text-red-700": isProblem,
            })}
          >
            {t(`projectPeriodConfirmation.criteria.${code}`)}
          </span>
          {detailText && (
            <span className="text-sm text-muted-foreground">{detailText}</span>
          )}
        </div>
      </div>
      <div className="sm:justify-self-end">
        {pathToView && projectId && (
          <Link
            to={generatePath(pathToView, { projectId })}
            className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
          >
            {t(`projectPeriodConfirmation.linkLabel.${code}`)}
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </li>
  );
};
